import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  InlineNotification,
  Loading,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
} from '@carbon/react';
import { formatMinutes, timesheetErrorKey, toIsoFromLocal } from 'src/components/Timesheet/timesheetFormat';
import { useTenantContext } from 'src/context/useTenantContext';
import { useMoney } from 'src/hooks/useMoney';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { TimesheetListRow, useCreateFirstEntry, useSetTimesheetPin, useTimesheetList } from 'src/hooks/useTimesheets';

const DATE = 'YYYY-MM-DD';
const STATUS_TAG = { OPEN: 'blue', APPROVED: 'green', PAID: 'gray' } as const;

function PinModal(props: { row: TimesheetListRow; onClose: () => void }) {
  const { t } = useTranslation();
  const { set, clear } = useSetTimesheetPin();
  const [pin, setPin] = useState('');
  const error = set.error ?? clear.error;
  return (
    <Modal
      open
      passiveModal
      modalHeading={`${t('timesheet.pin.label')} — ${props.row.employeeName}`}
      onRequestClose={props.onClose}
    >
      <TextInput
        id="timesheet-pin"
        type="password"
        inputMode="numeric"
        autoComplete="off"
        labelText={t('timesheet.pin.label')}
        helperText={t('timesheet.pin.hint')}
        value={pin}
        maxLength={6}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
      />
      {error && <InlineNotification kind="error" lowContrast hideCloseButton title={t(timesheetErrorKey(error))} />}
      {set.isSuccess && (
        <InlineNotification kind="success" lowContrast hideCloseButton title={t('timesheet.pin.saved')} />
      )}
      <Button
        disabled={pin.length < 4 || set.isLoading}
        onClick={() => set.mutate({ employeeId: props.row.employeeId, pin })}
      >
        {t('actions.save')}
      </Button>
      <Button
        kind="danger--ghost"
        disabled={clear.isLoading}
        onClick={() => clear.mutate(props.row.employeeId, { onSuccess: props.onClose })}
      >
        {t('timesheet.pin.clear')}
      </Button>
    </Modal>
  );
}

/** First shift for someone with no timesheet in this period yet — the detail page needs one to exist. */
function FirstEntryModal(props: { row: TimesheetListRow; tz: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateFirstEntry();
  const [day, setDay] = useState(dayjs().format(DATE));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const submit = () => {
    const start = toIsoFromLocal(day, from, props.tz);
    let end = toIsoFromLocal(day, to, props.tz);
    if (end <= start) end = toIsoFromLocal(dayjs(day).add(1, 'day').format(DATE), to, props.tz);
    create.mutate(
      { employeeId: props.row.employeeId, clockInAt: start, clockOutAt: end },
      { onSuccess: props.onClose }
    );
  };
  return (
    <Modal
      open
      passiveModal
      modalHeading={`${t('timesheet.actions.addEntry')} — ${props.row.employeeName}`}
      onRequestClose={props.onClose}
    >
      <TextInput
        id="first-date"
        type="date"
        labelText={t('timesheet.columns.date')}
        value={day}
        onChange={(e) => setDay(e.target.value)}
      />
      <TextInput
        id="first-in"
        type="time"
        labelText={t('timesheet.columns.clockIn')}
        value={from}
        onChange={(e) => setFrom(e.target.value)}
      />
      <TextInput
        id="first-out"
        type="time"
        labelText={t('timesheet.columns.clockOut')}
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      {create.error && (
        <InlineNotification kind="error" lowContrast hideCloseButton title={t(timesheetErrorKey(create.error))} />
      )}
      <Button disabled={!from || !to || create.isLoading} onClick={submit}>
        {t('actions.save')}
      </Button>
    </Modal>
  );
}

const EmployeeTimesheets = () => {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { buildPath } = useTenantContext();
  const restaurant = useCurrentRestaurant();
  const [date, setDate] = useState(dayjs().format(DATE));
  const [pinFor, setPinFor] = useState<TimesheetListRow | null>(null);
  const [firstEntryFor, setFirstEntryFor] = useState<TimesheetListRow | null>(null);
  const { data, isLoading } = useTimesheetList(date);

  const shift = (days: number) => {
    if (!data) return;
    setDate(
      dayjs(days < 0 ? data.period.start : data.period.end)
        .add(days, 'day')
        .format(DATE)
    );
  };

  return (
    <div className="timesheet-list">
      <div className="timesheet-list__toolbar">
        <Button kind="ghost" onClick={() => shift(-1)}>
          ←
        </Button>
        <strong>
          {data
            ? `${dayjs(data.period.start).format('DD/MM/YYYY')} – ${dayjs(data.period.end).format('DD/MM/YYYY')}`
            : ''}
        </strong>
        <Button kind="ghost" onClick={() => shift(1)}>
          →
        </Button>
        <Button kind="tertiary" onClick={() => navigate(buildPath('timesheets/kiosk'))}>
          {t('timesheet.kiosk.title')}
        </Button>
        <Button kind="tertiary" onClick={() => navigate(buildPath('timesheets/calendar'))}>
          {t('timesheet.calendar.open')}
        </Button>
      </div>

      {isLoading && <Loading withOverlay={false} />}
      {data && (
        <Table size="md">
          <TableHead>
            <TableRow>
              <TableHeader>{t('timesheet.columns.employee')}</TableHeader>
              <TableHeader>{t('timesheet.columns.hours')}</TableHeader>
              <TableHeader>{t('timesheet.columns.pay')}</TableHeader>
              <TableHeader>{t('timesheet.columns.status')}</TableHeader>
              <TableHeader>{t('timesheet.columns.confirmed')}</TableHeader>
              <TableHeader>{t('timesheet.columns.warnings')}</TableHeader>
              <TableHeader />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row) => {
              // A local const (not `row.timesheet!`) so the onClick below closes over an
              // already-narrowed value instead of re-asserting non-null on a nullable field.
              const ts = row.timesheet;
              return (
                <TableRow key={row.employeeId} data-testid={`timesheet-row-${row.employeeId}`}>
                  <TableCell>{row.employeeName}</TableCell>
                  <TableCell>{formatMinutes(row.payableMin)}</TableCell>
                  <TableCell>{money.format(row.pay)}</TableCell>
                  <TableCell>
                    {ts && <Tag type={STATUS_TAG[ts.status]}>{t(`timesheet.status.${ts.status}`)}</Tag>}
                  </TableCell>
                  <TableCell>{ts?.employeeConfirmedAt ? '✓' : '—'}</TableCell>
                  <TableCell>
                    {row.warnings.map((w) => (
                      <Tag key={w} size="sm" type="red">
                        {t(`timesheet.warnings.${w}`)}
                      </Tag>
                    ))}
                  </TableCell>
                  <TableCell>
                    {ts ? (
                      <Button
                        size="sm"
                        kind="ghost"
                        onClick={() => navigate(buildPath(`employees/timesheets/${ts.id}`))}
                      >
                        {t('timesheet.actions.open')}
                      </Button>
                    ) : (
                      <Button size="sm" kind="ghost" onClick={() => setFirstEntryFor(row)}>
                        {t('timesheet.actions.addEntry')}
                      </Button>
                    )}
                    <Button size="sm" kind="ghost" onClick={() => setPinFor(row)}>
                      {t('timesheet.pin.label')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {pinFor && <PinModal row={pinFor} onClose={() => setPinFor(null)} />}
      {firstEntryFor && (
        <FirstEntryModal
          row={firstEntryFor}
          tz={restaurant?.reportingTimezone ?? 'Asia/Ho_Chi_Minh'}
          onClose={() => setFirstEntryFor(null)}
        />
      )}
    </div>
  );
};

export default EmployeeTimesheets;
